import { useState, useCallback } from 'react';
import { Stack, Text, Button, Group, Alert, Card, Title, Divider, HtmlEditor } from '@/components/ui';
import { IconAlertCircle, IconUpload, IconFileText } from '@tabler/icons-react';
import { FileUploader } from '@/components/ccms-components/file-management';
import { useAiWorkflowService } from '@actschurch/ccms-sdk/react';
import { useAiWorkflowStore } from '../../../../stores/aiWorkflowStore';

const IMAGE_ACCEPT = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const AUDIO_ACCEPT = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp4'];

export default function UploadInput() {
  const workflowService = useAiWorkflowService();
  const {
    selectedPresetId,
    selectedPresetName,
    inputType,
    setActiveSession,
    setExtractedData,
    setValidationErrors,
    setCurrentStep,
    setIsProcessing,
    setError,
    error,
    reset,
  } = useAiWorkflowStore();

  const [uploadedFileRef, setUploadedFileRef] = useState<{ url?: string; id?: string; name?: string } | null>(null);
  const [inputText, setInputText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isTextInput = inputType.toLowerCase() === 'text';
  const isImageInput = inputType.toLowerCase() === 'image';
  const isAudioInput = inputType.toLowerCase() === 'audio';

  const acceptTypes = isImageInput
    ? IMAGE_ACCEPT
    : isAudioInput
      ? AUDIO_ACCEPT
      : IMAGE_ACCEPT;

  const canSubmit = isTextInput ? inputText.trim().length > 0 : uploadedFileRef !== null;

  const handleProcess = useCallback(async () => {
    if (!selectedPresetId) return;
    setSubmitting(true);
    setIsProcessing(true);
    setError(null);
    setCurrentStep('processing');

    try {
      let session;
      if (isTextInput) {
        session = await workflowService.startWithText({
          vendorID: 0,
          presetID: selectedPresetId,
          inputText,
        });
      } else if (uploadedFileRef) {
        session = await workflowService.startWithFile(
          0,
          selectedPresetId,
          new File([], uploadedFileRef.name ?? 'upload'),
        );
      } else {
        throw new Error('No input provided');
      }

      setActiveSession(session.sessionID);

      if (session.extractedJson) {
        try {
          const parsed = JSON.parse(session.extractedJson);
          setExtractedData(parsed);
        } catch {
          setExtractedData({ rawJson: session.extractedJson });
        }
      }

      if (session.validationErrors) {
        try {
          const errors = JSON.parse(session.validationErrors);
          setValidationErrors(Array.isArray(errors) ? errors : [session.validationErrors]);
        } catch {
          setValidationErrors([session.validationErrors]);
        }
      }

      setCurrentStep('review');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Workflow processing failed';
      setError(message);
      setCurrentStep('upload');
    } finally {
      setSubmitting(false);
      setIsProcessing(false);
    }
  }, [
    selectedPresetId, isTextInput, inputText, uploadedFileRef,
    workflowService, setActiveSession, setExtractedData,
    setValidationErrors, setCurrentStep, setIsProcessing, setError,
  ]);

  return (
    <Stack gap="lg">
      {/* Header with preset info */}
      <Group justify="space-between" wrap="wrap">
        <div>
          <Title order={4}>Provide Input</Title>
          <Text size="sm" c="dimmed">
            Preset: {selectedPresetName} ({inputType})
          </Text>
        </div>
        <Button variant="subtle" size="xs" onClick={reset}>
          Change Preset
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Input area in Card */}
      {isTextInput ? (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconFileText size={20} />
              <Title order={4}>Text Input</Title>
            </Group>
          </Group>
          <Divider mb="sm" />
          <HtmlEditor
            label="Input Text"
            description="Paste or type the text you want the AI to extract data from."
            placeholder="Enter your text here..."
            minHeight={160}
            value={inputText}
            onChange={(_html, text) => setInputText(text)}
          />
        </Card>
      ) : (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconUpload size={20} />
              <Title order={4}>Upload {inputType}</Title>
            </Group>
          </Group>
          <Divider mb="sm" />
          <FileUploader
            domain="AIManagement"
            recordId="new"
            accept={acceptTypes}
            maxSize={25 * 1024 * 1024}
            onUploadComplete={(files) => {
              const uploaded = files[0];
              if (uploaded) {
                setUploadedFileRef(uploaded);
              }
            }}
            onUploadError={() => {
              setError('File rejected. Please check the file type and size.');
            }}
          />
        </Card>
      )}

      {/* Action buttons */}
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={reset}>
          Back
        </Button>
        <Button
          onClick={handleProcess}
          loading={submitting}
          disabled={!canSubmit}
        >
          Process with AI
        </Button>
      </Group>
    </Stack>
  );
}
